// @ts-nocheck
import { useState, useRef, useCallback, useEffect, type ComponentType, type DragEvent, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Home, Headset, Clock, MessageSquare, Calendar, Users, TrendingUp,
  BotMessageSquare, Link2, Globe, Settings, PhoneCall, Voicemail,
  Search, Sun, Moon, ChevronRight, ChevronDown,
  Plus, Radio, CreditCard, MessageSquareWarning, BellRing, Phone,
} from 'lucide-react';
import { SwoftLogo } from '@/components/common/SwoftLogo';
import { RevcallLogo } from '@/components/common/RevcallLogo';
import { api } from '@/api/api-client';
// Correction #3 — la Power Hour vient du dialer et s'installe ici, dans le
// menu rapide de gauche : chrono + duree saisissable + resultats du jour.
import PowerHourPanel from '@/components/dialer/PowerHourPanel';
import { crmProviderShown } from '@/lib/hubspotUi';
import { useAuthStore } from '@/stores/authStore';
import { CustomerSwitcher } from './CustomerSwitcher';
import { SETTINGS_SECTIONS, resolveSettingsSection, settingsMenuRows } from '@/pages/dialer/settings/sections';
import { pathForView } from '@/layouts/dialerRoutes';
import { shortcutLabel } from '@/lib/platform';
import { useScrollCue } from '@/hooks/useScrollCue';

/**
 * XDialSidebar — VF design.
 * Screenshots ref : Page accueil dark mode, page taches dark mode.
 * - Largeur 200px (expanded) / 56px (collapsed)
 * - Brand "XDial" en haut + bouton Nouveau RDV violet + search + nav items + XP bas
 * - Drag & drop réordonnable (préservé)
 * - Shortcut ⌘B (préservé)
 * - Multi-client switcher (préservé)
 */

export const NAV_GROUPS = [
  // Tâches is in the main group (QW-d7, DESIGN.md §5): its counter was
  // computed but the entry was only reachable from the home page.
  { key: 'main', ids: ['home', 'queue', 'followups', 'call-history', 'sms'] },
  // 'conference' retiree (ADR-031) — la feature demande Caller.phone qui
  // n'existe plus dans le modele. Reactivable une fois que ParallelCall
  // expose customer_id au domaine et que le call a 3 a un endpoint propre.
  // 'booked-meeting' (2026-09-23 -> retiree 2026-09-27, meme demande client) --
  // ouvrait le dialog "renseigner un rendez-vous pris" depuis la nav. Le
  // second chemin (fiche prospect -> BookedMeetingForm) reste : voir
  // ProspectDetailPanel.tsx. BookedMeetingDialog.tsx et son etat dans
  // DialerLayout.tsx ne sont plus atteignables par aucun chemin ; a trancher.
  // 'meetings' et 'vm-drops' retirees du menu le 2026-09-30 : chacune ne
  // portait qu'un seul champ, le lien de prise de rendez-vous et le message
  // vocal. Les deux vivent desormais dans Reglages > General, ou ils sont a
  // cote des autres reglages au lieu d'occuper une entree de menu chacun.
  // 'tools' retire de la sidebar (demande produit 2026-04-20). La page reste
  // accessible via l'URL /ParallelDialer/tools — le composant n'est pas supprime.
  // #31 -- le module Objections rejoint le groupe d'analyse, a cote
  // d'Analytique : c'est une vue d'analyse, pas un outil d'appel.
  // 2026-10-08 -- 'objections' retire du menu (Kevin). Same treatment as
  // 'tools': the route /ParallelDialer/objections and its screen stay, only
  // the menu entry goes.
  // W4 (audit B2-objections, DESIGN.md §5): back in the menu, labelled
  // « Bêta » on the page, as in the new rail.
  { key: 'analyze', ids: ['analytics', 'objections'] },
  // 'crm' est de retour dans le menu (Kevin, 2026-10-01), sous le nom de ce
  // qu'elle montre : les OPPORTUNITES du CRM, rangees par statut. Le retrait
  // de #15 confondait deux choses -- se connecter a un CRM, qui arrive une
  // fois et vit dans Reglages > Integrations, et LIRE son pipeline, qui est
  // une vue de travail quotidienne. Seule la premiere avait sa place dans les
  // reglages ; c'est exactement ce que l'avertissement plus bas signalait.
  // Groupe a elle seule, demande de Kevin : ni un ecran d'appel, ni une vue
  // d'analyse. Un groupe pose un separateur au-dessus de lui, donc celui-ci
  // tombe sous « Objections ».
  { key: 'pipeline', ids: ['crm'] },
  // 'billing' deplace dans le menu avatar (UserHeaderBar) pour designer un
  // pattern "compte/admin" hors de la nav principale dialer.
  // #15 -- 'crm' retire de la barre : le client veut l'acces au CRM dans les
  // Parametres uniquement ("on ne change pas de CRM souvent"). Meme traitement
  // que 'tools' plus haut : **la route /ParallelDialer/crm et le composant
  // restent** -- seule l'entree de menu disparait, le retrait est reversible.
  // ⚠️ Cette page n'est pas qu'un ecran de connexion : elle porte le pipeline,
  // les deals, les contacts et l'activite. C'est une vue de travail qui quitte
  // le menu.
  { key: 'system', ids: ['client-portal', 'settings'] },
];

type IconType = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

interface TabMeta {
  labelKey: string;
  icon: IconType;
}

/**
 * Le logo Swoft a la place d'une icone de trait : cette entree designe LE CRM
 * de Swoft, pas une fonction generique du produit. Il garde ses couleurs, donc
 * il ignore celle que la barre applique aux autres icones -- un logo recolore
 * en gris n'est plus le logo.
 */
const SwoftNavIcon: IconType = ({ size, style }) => (
  <span style={{ ...style, color: undefined, display: 'inline-flex', flexShrink: 0 }}>
    <SwoftLogo size={typeof size === 'number' ? size : 15} />
  </span>
);

/**
 * The main action of both menus (SR2-6): « Appeler », or « Reprendre la
 * liste » when a session is paused on a list.
 */
export function primaryCallLabelKey(listPaused: boolean): string {
  return listPaused ? 'dialer:nav.resumeList' : 'dialer:nav.call';
}

export const TAB_META: Record<string, TabMeta> = {
  home:           { labelKey: 'home',       icon: Home },
  queue:          { labelKey: 'dialer',     icon: Headset },
  'call-history': { labelKey: 'history',    icon: PhoneCall },
  followups:      { labelKey: 'tasks',      icon: Clock },
  sms:            { labelKey: 'messaging',  icon: MessageSquare },
  analytics:      { labelKey: 'analytics',  icon: TrendingUp },
  objections:     { labelKey: 'objections', icon: MessageSquareWarning },
  tools:          { labelKey: 'assistant',  icon: BotMessageSquare },
  crm:            { labelKey: 'opportunities', icon: SwoftNavIcon },
  'client-portal':{ labelKey: 'portal',     icon: Globe },
  billing:        { labelKey: 'billing',    icon: CreditCard },
  settings:       { labelKey: 'settings',   icon: Settings },
};

/* #50 -- les sous-sections des parametres, dans l'ordre demande par le client.
   Plusieurs existaient deja sous un autre libelle : on pointe dessus au lieu
   d'en creer des doublons. */
/* 2026-09-15 -- 'fieldmapping' etait liste DEUX fois, sous deux libelles
   differents ('Call dispositions' et 'Field mapping'). Deux entrees, un seul
   panneau : cliquer l'une ou l'autre ouvrait le meme ecran, les deux
   s'activaient ensemble, et React refusait la cle dupliquee
   ('Encountered two children with the same key, `fieldmapping`', emis a chaque
   rendu du menu). Une seule entree desormais.

   ⚠️ CORRIGE le 2026-09-15, apres relecture du PDF source : la 1re passe avait
   garde le libelle « Field mapping » et laisse tomber « Call dispositions ».
   C'etait le mauvais choix. #50 enumere NEUF sous-sections et nomme les deux
   separement, et #53 (« Matching de proprietes CRM → XDIAL », ref. Minari
   « Call dispositions mapping ») decrit exactement le panneau qui existe : il
   fait correspondre chaque DISPOSITION D'APPEL a une valeur CRM. Ce panneau
   est donc « Call dispositions », pas « Field mapping ».

   ⚠️ Il manque donc toujours la 9e section : « Field mapping » au sens du
   client — la correspondance des PROPRIETES de contact (CRM → XDIAL), qui
   n'est pas la meme chose que les dispositions d'appel. Aucune doleance ne la
   detaille ; construire ce panneau est un chantier, pas une correction.
   Cf. BUGS-OUVERTS.md (MENU-1). */
// 2026-09-27 -- 'customfields', 'fieldmapping', 'phonefields', 'incoming' et
// 'leadstatuses' retires : les cinq panneaux qu'ils ouvraient n'ecrivaient
// que dans localStorage, ne repondaient a aucun endpoint et n'etaient lus par
// personne (SettingsSectionsCat10.tsx et CustomFieldsSettings.tsx,
// supprimes). Une entree de menu qui promet un reglage qui ne regle rien est
// pire que son absence.
// Labels come from settings:sections.<id>, the same keys SettingsScreen uses
// for its own section list: one catalog entry, two menus.
// Toutes les sections de l'ecran Reglages, dans le meme ordre que lui. Il en
// manquait trois, dont « General », ou vivent desormais la langue, le lien de
// prise de rendez-vous et le message vocal : une section absente du menu ne se
// trouve qu'en ouvrant Reglages puis en cherchant, ce qui revient a la cacher.
// W4 (F6): the catalogue now lives in pages/dialer/settings/sections.ts, with
// each section's group (Mon compte / Organisation); both menus print a group
// heading before the first section of each group.
export const SOUS_SECTIONS_PARAMETRES = SETTINGS_SECTIONS;

export const TAB_TOUR_ATTR: Record<string, string> = {
  analytics: 'analytics-nav',
  tools: 'ai-nav',
  settings: 'settings-nav',
};

interface SidebarStats {
  queuedProspects?: number;
  followupsCount?: number;
  /** Tâches badge: overdue callbacks of the active customer (W4, B2-followups). */
  overdueFollowupsCount?: number;
}

interface XDialSidebarProps {
  selectedView: string;
  onViewChange: (id: string) => void;
  stats?: SidebarStats;
  /** A session is paused on a list: the main action reads « Reprendre la liste » (SR2-6). */
  listPaused?: boolean;
  theme: string;
  cycleTheme: () => void;
  callerName?: string;
  onLogout: () => void;
  navTabOrder?: string[];
  onNavTabOrderChange?: (order: string[]) => void;
  collapsed: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onSearchOpen?: () => void;
  onTourStart?: () => void;
  onLiveFeedOpen?: () => void;
  // ADR-024 + ADR-031 — Customer (B2B client of the tenant) is the only
  // sidebar switcher. selectedCustomerId === null means no scoping.
  // The pre-ADR-024 Tenant switcher (clientEmail / allClients / onClientChange)
  // and its ClientLite shape have been removed.
  customers?: Array<{ id: string; company_name: string; contact_name?: string; active_crm_provider?: string }>;
  selectedCustomerId?: string | null;
  onCustomerChange?: (customerId: string | null) => void;
}

interface FlatTabBase {
  type: 'tab';
  id: string;
  labelKey: string;
  icon: IconType;
  group: string;
}
interface FlatSeparator {
  type: 'separator';
  key: string;
}
type FlatItem = FlatTabBase | FlatSeparator;

export default function XDialSidebar({
  selectedView,
  onViewChange,
  stats,
  listPaused = false,
  theme,
  cycleTheme,
  callerName,
  onLogout,
  navTabOrder,
  onNavTabOrderChange,
  collapsed,
  onCollapsedChange,
  onSearchOpen,
  onTourStart,
  onLiveFeedOpen,
  customers,
  selectedCustomerId,
  onCustomerChange,
}: XDialSidebarProps) {
  const open = !collapsed;
  const w = open ? 200 : 56;
  const isDark = theme === 'dark';
  const { t } = useTranslation(['dialer', 'common', 'settings']);
  // Settings section shown in the page, read from ?section=. Kept in state so
  // the highlighted sub-item follows the page, which announces each change
  // with the xdial-settings-section event.
  const lireSection = () => {
    try { return new URLSearchParams(window.location.search).get('section'); } catch { return null; }
  };
  const [sectionParametres, setSectionParametres] = useState<string | null>(lireSection);
  useEffect(() => {
    const relire = () => setSectionParametres(lireSection());
    relire();
    window.addEventListener('xdial-settings-section', relire);
    return () => window.removeEventListener('xdial-settings-section', relire);
  }, [selectedView]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        onCollapsedChange?.(!collapsed);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [collapsed, onCollapsedChange]);

  // "Contacter Kevin": the help box at the bottom of the menu opens this modal.
  const [dragItem, setDragItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  const [hoverTab, setHoverTab] = useState<string | null>(null);
  const dragNodeRef = useRef<HTMLElement | null>(null);

  // ADR-024 — la selection est obligatoire : faute de selection valide, le
  // premier client fait foi. Resolu ici une seule fois, parce que le menu ET
  // le selecteur plus bas en dependent, et qu'ils doivent designer le meme.
  const clientActif = customers && customers.length > 0
    ? ((selectedCustomerId && customers.find((c) => c.id === selectedCustomerId)) || customers[0])
    : null;

  // 🚨 Kevin, 2026-10-01 : l'entree « CRM Swoft » ne s'affiche que si ce
  // client a un CRM connecte. Sans cela, le menu promettait un ecran qui ne
  // pouvait rien montrer. La ROUTE reste atteignable par son URL, et elle
  // mene alors a « Aucun CRM connecte » avec le bouton vers les reglages :
  // cacher l'entree n'est pas un controle d'acces.
  // A HubSpot customer has no entry while HubSpot is hidden from the UI
  // (AUDIT-INT-08, src/lib/hubspotUi.ts).
  const crmConnecte = crmProviderShown(clientActif?.active_crm_provider) !== 'none';

  const isAdminLike = useAuthStore((st) => st.user?.isSuperAdmin === true || st.activeMembership?.role === 'tenant_admin');
  const DEFAULT_ORDER = NAV_GROUPS.flatMap(g => g.ids);
  const orderedIds = navTabOrder && navTabOrder.length > 0 ? navTabOrder : DEFAULT_ORDER;

  const flatTabs: FlatItem[] = [];
  let prevGroup: string | null = null;
  NAV_GROUPS.forEach(group => {
    // Une entree ABSENTE de l'ordre enregistre passe a la fin de son groupe,
    // pas au debut. `indexOf` rend -1 pour elle : compare tel quel, ce -1 la
    // faisait remonter devant tout le reste, si bien qu'une entree ajoutee
    // apres coup s'affichait en premier chez quiconque avait deja reordonne
    // son menu -- et seulement chez ceux-la, ce qui rend le defaut invisible
    // depuis un poste neuf. Le tri etant stable, elles gardent entre elles
    // l'ordre declare dans NAV_GROUPS.
    const rang = (id: string) => {
      const i = orderedIds.indexOf(id);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    const ids = group.ids
      .filter((id) => id !== 'crm' || crmConnecte)
      // The portal entry is for admins; the route was already admin-only (QW-d7).
      .filter((id) => id !== 'client-portal' || isAdminLike)
      .sort((a, b) => rang(a) - rang(b));
    ids.forEach(id => {
      if (prevGroup && prevGroup !== group.key) {
        flatTabs.push({ type: 'separator', key: `sep-${group.key}` });
      }
      const meta = TAB_META[id];
      if (meta) flatTabs.push({ type: 'tab', id, labelKey: meta.labelKey, icon: meta.icon, group: group.key });
      prevGroup = group.key;
    });
  });

  const handleDragStart = useCallback((e: DragEvent<HTMLElement>, itemId: string) => {
    setDragItem(itemId);
    dragNodeRef.current = e.target as HTMLElement;
    (e.target as HTMLElement).style.opacity = '0.4';
    e.dataTransfer.effectAllowed = 'move';
  }, []);
  const handleDragOver = useCallback((e: DragEvent<HTMLElement>, itemId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (itemId !== dragItem) setDragOverItem(itemId);
  }, [dragItem]);
  const handleDrop = useCallback((e: DragEvent<HTMLElement>, targetId: string) => {
    e.preventDefault();
    if (!dragItem || dragItem === targetId) return;
    const newOrder = [...orderedIds];
    const fromIdx = newOrder.indexOf(dragItem);
    const toIdx = newOrder.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragItem);
    onNavTabOrderChange?.(newOrder);
    setDragItem(null);
    setDragOverItem(null);
  }, [dragItem, orderedIds, onNavTabOrderChange]);
  const handleDragEnd = useCallback(() => {
    if (dragNodeRef.current) dragNodeRef.current.style.opacity = '1';
    setDragItem(null);
    setDragOverItem(null);
  }, []);

  const tabCounts: Record<string, number | undefined> = {
    queue: stats?.queuedProspects,
    followups: stats?.overdueFollowupsCount,
  };

  // ADR-036: caller notifications (today: a no-show at the first meeting of
  // an opportunity, reported by Swoft). Polled, small, dismissed on click.
  const queryClient = useQueryClient();
  const navScrollRef = useRef<HTMLDivElement>(null);
  const moreBelow = useScrollCue(navScrollRef, [selectedView, open, sectionParametres]);
  const { data: callerNotifications = [] } = useQuery({
    queryKey: ['caller-notifications'],
    queryFn: () => api.functions.invoke('getMyNotifications').then((r) => (r?.data ?? r)?.notifications ?? []),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const unreadCount = callerNotifications.length;
  const dismissNotifications = useCallback(async () => {
    try {
      await api.functions.invoke('markAllNotificationsRead');
    } finally {
      queryClient.invalidateQueries({ queryKey: ['caller-notifications'] });
    }
  }, [queryClient]);

  return (
    <div
      data-tour="sidebar"
      className="flex flex-col h-screen flex-shrink-0 border-r"
      style={{
        width: w,
        background: 'var(--vf-surface-1)',
        // Correction #87 — la separation menu / contenu existait deja mais en
        // --vf-border (#E7E7EA), invisible sur fond blanc. Ton renforce, plus
        // un liseré interne clair : le menu se lit comme un encart, pas comme
        // une zone qui bave dans le contenu.
        borderColor: 'var(--vf-border-hi)',
        boxShadow: 'inset -1px 0 0 var(--vf-surface-2)',
        padding: open ? '12px 10px' : '12px 6px',
        gap: 3,
        transition: 'width 0.2s ease',
      }}
    >
      {/* Brand */}
      <div className="flex items-center" style={{ gap: 8, marginBottom: 6, padding: '2px 4px' }}>
        <div
          className="flex items-center justify-center cursor-pointer"
          style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--vf-accent-bg)' }}
          onClick={() => onViewChange('queue')}
        >
          <RevcallLogo size={16} color="var(--vf-accent)" />
        </div>
        {open && (
          <span className="flex-1" style={{ fontSize: 14, fontWeight: 700, color: 'var(--vf-text-1)', letterSpacing: '-0.01em' }}>
            XDial
          </span>
        )}
        <button
          onClick={() => onCollapsedChange?.(!collapsed)}
          title={open ? t('dialer:xdialSidebar.collapse') : t('dialer:xdialSidebar.expand')}
          className="flex items-center justify-center shrink-0"
          style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--vf-surface-2)', border: '1px solid var(--vf-border)', cursor: 'pointer' }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--vf-text-3)" strokeWidth="2.5" strokeLinecap="round">
            <path d={open ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
          </svg>
        </button>
      </div>


      {/* SR2-6 : l'action principale est d'appeler (la liste) ; « Nouveau RDV »
          devient secondaire. Fond --vf-primary, AA avec du blanc (DES-9a). */}
      <button
        onClick={() => onViewChange('queue')}
        className="w-full flex items-center transition-colors"
        style={{
          padding: open ? '8px 10px' : '8px 0',
          borderRadius: 8,
          border: 'none',
          background: 'var(--vf-primary)',
          color: 'var(--vf-primary-fg)',
          gap: 8,
          cursor: 'pointer',
          marginBottom: 3,
          fontWeight: 600,
          fontSize: 12,
          justifyContent: open ? 'flex-start' : 'center',
        }}
        title={open ? undefined : t(primaryCallLabelKey(listPaused))}
      >
        <Phone size={14} style={{ flexShrink: 0 }} strokeWidth={2.5} />
        {open && <span>{t(primaryCallLabelKey(listPaused))}</span>}
      </button>
      <button
        onClick={() => onViewChange('new-meeting')}
        className="w-full flex items-center transition-colors"
        style={{
          padding: open ? '7px 10px' : '7px 0',
          borderRadius: 8,
          border: '1px solid var(--vf-border)',
          background: 'transparent',
          color: 'var(--vf-text-1)',
          gap: 8,
          cursor: 'pointer',
          marginBottom: 3,
          fontWeight: 500,
          fontSize: 12,
          justifyContent: open ? 'flex-start' : 'center',
        }}
        title={open ? undefined : t('dialer:nav.newMeeting').replace(/^\+\s*/, '')}
      >
        <Plus size={14} style={{ flexShrink: 0 }} strokeWidth={2.5} />
        {open && <span>{t('dialer:nav.newMeeting').replace(/^\+\s*/, '')}</span>}
      </button>

      {/* Active client, right under the "Nouveau RDV" button (Kevin,
          2026-10-08). Correction #88 had moved it to the bottom of the menu,
          above the controls; it comes back up, but below the CTA rather than
          above it as before #88. */}
      {/* Customer switcher (ADR-024) — replaces the legacy "Client" (= Tenant)
          switcher. Lists Customers (B2B clients of the active tenant) and
          scopes the entire dialer view (prospects, calls, meetings,
          analytics, portal) to the selected one. "Tous les customers" =
          tenant-wide unfiltered view (default).
          Hidden when the active tenant has no customers yet. */}
      <CustomerSwitcher
        customers={customers}
        selectedCustomerId={selectedCustomerId}
        onCustomerChange={onCustomerChange}
        open={open}
      />

      {/* Caller notifications (ADR-036): shown only while something is unread */}
      {unreadCount > 0 && (
        <button
          onClick={dismissNotifications}
          title={callerNotifications.map((n) => n.message).join('\n')}
          className="w-full flex items-start text-left"
          style={{
            padding: open ? '7px 10px' : '7px 0',
            borderRadius: 8,
            background: 'var(--vf-surface-2)',
            border: '1px solid var(--vf-border)',
            gap: 8,
            cursor: 'pointer',
            marginBottom: 4,
            justifyContent: open ? 'flex-start' : 'center',
          }}
        >
          <BellRing size={13} style={{ color: 'var(--vf-accent)', flexShrink: 0, marginTop: 1 }} />
          {open && (
            <span className="flex-1" style={{ fontSize: 11, color: 'var(--vf-text-2)', lineHeight: 1.3 }}>
              {callerNotifications[0]?.message}
              {unreadCount > 1 && (
                <span className="vf-font-mono" style={{ marginLeft: 6, fontSize: 9, color: 'var(--vf-text-3)' }}>
                  +{unreadCount - 1}
                </span>
              )}
            </span>
          )}
          {!open && (
            <span
              className="vf-font-mono"
              style={{ fontSize: 9, fontWeight: 600, color: 'var(--vf-accent)' }}
            >
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Nav tabs */}
      <div ref={navScrollRef} className="relative flex flex-col flex-1 min-h-0 w-full" style={{ gap: 1, overflowY: 'auto' }}>
        {flatTabs.map((item) => {
          if (item.type === 'separator') {
            return <div key={item.key} style={{ height: 1, background: 'var(--vf-border)', margin: '5px 0' }} />;
          }
          const Icon = item.icon;
          const isActive = selectedView === item.id;
          const isHovered = hoverTab === item.id && !isActive;
          const count = tabCounts[item.id];
          const tourAttr = TAB_TOUR_ATTR[item.id];

          // QA-W4-8: a link (opens in a new tab, middle click, copy the
          // address); a plain click still navigates in place.
          return (
            <a
              key={item.id}
              href={pathForView(item.id)}
              aria-current={isActive ? 'page' : undefined}
              data-tour={tourAttr || undefined}
              onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                onViewChange(item.id);
              }}
              draggable
              onDragStart={(e) => handleDragStart(e, item.id)}
              onDragOver={(e) => handleDragOver(e, item.id)}
              onDrop={(e) => handleDrop(e, item.id)}
              onDragEnd={handleDragEnd}
              onMouseEnter={() => setHoverTab(item.id)}
              onMouseLeave={() => setHoverTab(null)}
              title={t(`dialer:nav.${item.labelKey}`)}
              className="w-full flex items-center cursor-pointer active:cursor-grabbing relative no-underline"
              style={{
                padding: open ? '7px 10px' : '7px 0',
                borderRadius: 8,
                border: 'none',
                fontSize: 12.5,
                fontWeight: isActive ? 600 : 500,
                background: isActive
                  ? 'var(--vf-surface-3)'
                  : isHovered
                    ? 'var(--vf-surface-2)'
                    : 'transparent',
                color: isActive ? 'var(--vf-text-1)' : 'var(--vf-text-2)',
                transition: 'all 0.12s',
                gap: 10,
                justifyContent: open ? 'flex-start' : 'center',
              }}
            >
              {/* barre active gauche */}
              {isActive && (
                <span
                  style={{
                    position: 'absolute',
                    left: 2,
                    top: 6,
                    bottom: 6,
                    width: 2,
                    borderRadius: 2,
                    background: 'var(--vf-accent)',
                  }}
                />
              )}
              <Icon size={15} style={{ flexShrink: 0, color: isActive ? 'var(--vf-text-1)' : 'var(--vf-text-3)' }} />
              {open && <span className="flex-1 text-left">{t(`dialer:nav.${item.labelKey}`)}</span>}
              {open && count !== undefined && count > 0 && (
                <span
                  className="vf-font-mono"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: isActive ? 'var(--vf-accent)' : 'var(--vf-text-3)',
                    padding: '1px 6px',
                    borderRadius: 5,
                    background: isActive ? 'var(--vf-accent-bg)' : 'var(--vf-surface-3)',
                  }}
                >
                  {count}
                </span>
              )}
              {/* Settings unfolds its sections below it: the chevron says so. */}
              {open && item.id === 'settings' && (
                <ChevronRight
                  size={13}
                  style={{
                    flexShrink: 0,
                    color: 'var(--vf-text-3)',
                    transform: isActive ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                />
              )}
            </a>
          );
        })}
        {/* #50 -- les sous-sections des parametres, depliees sous l'entree
            quand elle est active. Elles pointent sur ?section=... que
            SettingsScreen sait lire. */}
        {selectedView === 'settings' && open && (
          <div className="flex flex-col" style={{ gap: 1, paddingLeft: 26, paddingBottom: 4 }}>
            {settingsMenuRows().map((row) => {
              if (row.kind === 'group') {
                return (
                  <p
                    key={`group-${row.group}`}
                    className="uppercase"
                    style={{ padding: '6px 8px 2px', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--vf-text-3)' }}
                  >
                    {t(`settings:groups.${row.group}`)}
                  </p>
                );
              }
              const ss = row.entry;
              const courante = resolveSettingsSection(sectionParametres) === ss.id;
              return (
                <button
                  key={ss.id}
                  aria-current={courante ? 'true' : undefined}
                  onClick={() => {
                    try {
                      const u = new URL(window.location.href);
                      u.searchParams.set('section', ss.id);
                      window.history.replaceState(window.history.state, '', u.toString()); // keep React Router's state (key, idx)
                      window.dispatchEvent(new Event('xdial-settings-section'));
                    } catch { /* URL indisponible */ }
                  }}
                  className="w-full text-left"
                  style={{
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: 'none',
                    fontSize: 11.5,
                    fontWeight: courante ? 600 : 400,
                    color: courante ? 'var(--vf-accent)' : 'var(--vf-text-2)',
                    background: courante ? 'var(--vf-surface-2)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  {t(`settings:sections.${ss.id}`)}
                </button>
              );
            })}
          </div>
        )}
        {/* QA-W4-8: entries below the fold (the settings sections at 900 px
            high) were hidden under the Power Hour card with no hint. */}
        {moreBelow && (
          <div aria-hidden data-testid="nav-more-below" className="pointer-events-none sticky bottom-0 -mt-6 flex h-6 shrink-0 items-end justify-center" style={{ background: 'linear-gradient(to bottom, transparent, var(--vf-surface-1))' }}>
            <ChevronDown size={12} style={{ color: 'var(--vf-text-3)' }} />
          </div>
        )}
      </div>

      {/* Bottom section */}
      <div className="flex flex-col" style={{ gap: 4, marginTop: 4 }}>
        <div style={{ height: 1, background: 'var(--vf-border)', margin: '2px 0' }} />

      {/* Correction #3 — Power Hour. Elle occupait une barre d'outils du
          dialer, ou son bouton lancait les appels. Elle est desormais ici,
          dans le menu de gauche, au-dessus de la recherche : un chrono, une
          duree libre, et les resultats du jour. */}
      <div style={{ marginBottom: 6 }}>
        <PowerHourPanel collapsed={!open} />
      </div>


        {/* Search, at the bottom of the menu since 2026-10-08 (Kevin). ⌘K
            still opens it from anywhere. */}
      <button
        onClick={() => onSearchOpen?.()}
        className="w-full flex items-center"
        style={{
          padding: open ? '7px 10px' : '7px 0',
          borderRadius: 8,
          background: 'var(--vf-surface-2)',
          border: '1px solid var(--vf-border)',
          gap: 8,
          cursor: 'pointer',
          justifyContent: open ? 'flex-start' : 'center',
        }}
      >
        <Search size={13} style={{ color: 'var(--vf-text-3)', flexShrink: 0 }} />
        {open && (
          <>
            <span className="flex-1 text-left" style={{ fontSize: 11, color: 'var(--vf-text-3)' }}>
              {t('common:actions.search', 'Rechercher...')}
            </span>
            <span className="vf-font-mono" style={{ fontSize: 11, color: 'var(--vf-text-3)' }}>{shortcutLabel('K')}</span>
          </>
        )}
      </button>

        {/* #68 support box ("Besoin d'aide ?", Contacter Kevin) removed from
            the menu on 2026-10-08 (Kevin). ContactKevinModal still opens from
            the no-customer screen (DialerLayout) and the back office. */}

        {/* Controls row */}
        {open && (
          <div className="flex items-center justify-end" style={{ gap: 4, padding: '0 2px' }}>
            <button
              title={t('dialer:nav.liveFeed')}
              onClick={() => onLiveFeedOpen?.()}
              className="flex items-center justify-center"
              style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--vf-border)', background: 'transparent', cursor: 'pointer', color: 'var(--vf-text-3)' }}
            >
              <Radio size={11} />
            </button>
            <button
              onClick={cycleTheme}
              title={isDark ? t('dialer:xdialSidebar.lightMode') : t('dialer:xdialSidebar.darkMode')}
              className="flex items-center justify-center"
              style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--vf-border)', background: 'transparent', cursor: 'pointer' }}
            >
              {isDark ? <Sun size={11} style={{ color: 'var(--vf-text-3)' }} /> : <Moon size={11} style={{ color: 'var(--vf-text-3)' }} />}
            </button>
            <button
              data-tour="tour-trigger"
              onClick={() => onTourStart?.()}
              title={t('dialer:nav.tourGuide')}
              className="flex items-center justify-center"
              style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid var(--vf-border)', background: 'transparent', cursor: 'pointer', color: 'var(--vf-text-3)', fontSize: 11, fontWeight: 700 }}
            >
              ?
            </button>
          </div>
        )}

        {/* User avatar + menu deplace dans le header (XDialTopBar). */}
      </div>
    </div>
  );
}
